import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { YStack, XStack, View, Text, Spinner } from "tamagui";
import { RiArrowRightSLine } from "react-icons/ri";

import SkinV2Screen from "@components/layout/SkinV2Screen";
import { v2 } from "@screen/Home/pages/GasShop/skin";
import { colors } from "@styles/colors";
import api from "@services/axios/apiManager";
import { useCusAuth } from "@utils/store/auth/useCusAuth";

import SectionLabel from "./components/ui/SectionLabel";
import ListContainer from "./components/ui/ListContainer";
import InfoNote from "./components/ui/InfoNote";
import AccountFilterChip from "./components/payRecord/AccountFilterChip";
import type { AccountFilterOption } from "./components/payRecord/types";
import { listPaymentRecords, orderStatusLabel, type PaymentRecordItem } from "./API/handleAccountHistoryAPI";
import { isOrderAmountHidden } from "@utils/func/orderAmountVisibility";
import { track } from "@lib/tracker";

const PAGE_SIZE = 20;

const fmtDate = (iso: string): string => {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())}`;
};
const fmtTime = (iso: string): string => {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
};

interface DateGroup {
  date: string;
  items: PaymentRecordItem[];
}
const groupByDate = (items: ReadonlyArray<PaymentRecordItem>): DateGroup[] => {
  const groups: DateGroup[] = [];
  const idx = new Map<string, number>();
  for (const it of items) {
    const key = fmtDate(it.created_at);
    const at = idx.get(key);
    if (at === undefined) {
      idx.set(key, groups.length);
      groups.push({ date: key, items: [it] });
    } else {
      groups[at] = { ...groups[at], items: [...groups[at].items, it] };
    }
  }
  return groups;
};

/**
 * 帳號付費紀錄。串接 GET /app-api/customer/payment-records（caller 視角 placed_by；可選 context_id 過濾）。
 */
export const PayRecord = () => {
  const navigate = useNavigate();
  const { cusJwtToken } = useCusAuth();

  const [accounts, setAccounts] = useState<AccountFilterOption[]>([]);
  const [accountFilter, setAccountFilter] = useState<string | null>(null);

  const [items, setItems] = useState<PaymentRecordItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  // 鏡射目前篩選，供 loadMore 在 await 後判斷篩選是否已被切換（避免把舊篩選結果接到新清單）。
  const filterRef = useRef<string | null>(accountFilter);
  useEffect(() => {
    filterRef.current = accountFilter;
  }, [accountFilter]);

  // 帳戶篩選選項（取自 /contexts）
  useEffect(() => {
    if (!cusJwtToken) return;
    let active = true;
    api
      .get("/app-api/customer/contexts", { headers: { Authorization: `Bearer ${cusJwtToken}` } })
      .then((res) => {
        if (!active) return;
        const list = (res.data?.data?.contexts ?? []) as Array<{
          context_id: number;
          context_name: string;
          label?: string;
          primary_supplier?: { supplier_name?: string };
        }>;
        setAccounts(
          list.map((c) => ({
            id: String(c.context_id),
            label: c.label || c.context_name,
            vendor: c.primary_supplier?.supplier_name,
          })),
        );
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [cusJwtToken]);

  // 付費紀錄（第一頁；切換帳戶過濾時重抓）
  useEffect(() => {
    if (!cusJwtToken) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setPage(1);
    setReachedEnd(false);
    listPaymentRecords({
      jwtToken: cusJwtToken,
      params: { page: 1, limit: PAGE_SIZE, context_id: accountFilter ? Number(accountFilter) : undefined },
    })
      .then((r) => {
        if (!active) return;
        if (r.state === "success") {
          const list = r.data?.items ?? [];
          setItems(list);
          setTotal(r.data?.total ?? 0);
          // 回傳不足一頁 → 已到底，避免之後無限「載入更多」
          setReachedEnd(list.length < PAGE_SIZE);
        } else {
          setItems([]);
          setTotal(0);
          setReachedEnd(true);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [cusJwtToken, accountFilter]);

  const loadMore = async () => {
    if (!cusJwtToken || loadingMore) return;
    const filterAtCall = accountFilter;
    const next = page + 1;
    setLoadingMore(true);
    const r = await listPaymentRecords({
      jwtToken: cusJwtToken,
      params: { page: next, limit: PAGE_SIZE, context_id: filterAtCall ? Number(filterAtCall) : undefined },
    });
    // 篩選在載入途中被切換 → 丟棄這次結果（避免接到新清單）
    if (filterRef.current !== filterAtCall) {
      setLoadingMore(false);
      return;
    }
    if (r.state === "success") {
      const list = r.data?.items ?? [];
      setItems((prev) => [...prev, ...list]);
      setPage(next);
      if (list.length < PAGE_SIZE) setReachedEnd(true);
    }
    setLoadingMore(false);
  };

  const hasMore = !reachedEnd && items.length < total;
  const groups = useMemo(() => groupByDate(items), [items]);

  /* 淡霧頁首：一行說明 + 一句總筆數。
     原本的「共筆數」是一張佔滿整列的半透明卡，裡面只有一個數字——版面很大、資訊很少；
     在 V2 收成一行白字即可。 */
  const subHeader = (
    <YStack mb={16}>
      <Text color={v2.hazeSub} fontSize={13}>
        {accountFilter === null ? "跨帳戶 · 你經手的付款" : "單一帳戶付費紀錄"}
      </Text>
      <XStack ai="baseline" gap="$1.5" mt="$1.5">
        <Text color={v2.hazeInk} fontSize={20} fontWeight="800" style={{ fontVariantNumeric: "tabular-nums" }}>
          {total}
        </Text>
        <Text color={v2.hazeSub} fontSize={12}>
          筆紀錄
        </Text>
      </XStack>
    </YStack>
  );

  return (
    /* band="short"：標題列 + 兩行白字就結束，用標準長度的話下半段藍色帶是空的 */
    <SkinV2Screen title="付費紀錄" onBack={() => navigate("/account")} subHeader={subHeader} band="short">
      <YStack className="gas-stagger-kids" gap="$3.5">
        <AccountFilterChip accounts={accounts} selectedId={accountFilter} onSelect={setAccountFilter} />

        {loading ? (
          <YStack ai="center" jc="center" py="$10">
            <Spinner size="large" color={colors.primary} />
          </YStack>
        ) : groups.length === 0 ? (
          <YStack ai="center" jc="center" py="$10">
            <Text fontSize={14} color={v2.body}>
              目前沒有付費紀錄
            </Text>
          </YStack>
        ) : (
          <>
            {groups.map((g) => (
              <YStack key={g.date} gap="$2">
                <Text fontSize={11.5} fontWeight="700" color={v2.label} pl="$1" ls={0.3}>
                  {g.date}
                </Text>
                <ListContainer>
                  {g.items.map((it, i) => (
                    <XStack
                      key={`${it.platform_order_id}-${i}`}
                      ai="center"
                      gap="$3"
                      px="$3.5"
                      py="$3"
                      bbw={i === g.items.length - 1 ? 0 : 1}
                      bbc={v2.divider}
                      cursor="pointer"
                      pressStyle={{ opacity: 0.7 }}
                      onPress={() => {
                        track("payrecord_list_click", {});
                        navigate(`/account/payRecord/${it.platform_order_id}`, { state: { item: it } });
                      }}>
                      <YStack f={1} miw={0}>
                        <Text fontSize={14} fontWeight="700" color={v2.title} numberOfLines={1}>
                          {it.display_title}
                        </Text>
                        <Text fontSize={11} color={v2.sub} mt="$0.5" numberOfLines={1}>
                          {fmtTime(it.paid_at ?? it.created_at)}
                          {it.display_subtitle ? ` · ${it.display_subtitle}` : ""}
                        </Text>
                      </YStack>
                      <YStack ai="flex-end" flexShrink={0}>
                        {!isOrderAmountHidden(it.hide_amount_on_pending, it.order_status) && (
                          <Text fontSize={15} fontWeight="800" color={v2.title} style={{ fontVariantNumeric: "tabular-nums" }}>
                            ${(it.amount ?? 0).toLocaleString()}
                          </Text>
                        )}
                        <Text fontSize={10} fontWeight="600" color={colors.sub} mt="$0.5">
                          {orderStatusLabel(it.order_status)}
                        </Text>
                      </YStack>
                      <RiArrowRightSLine size={14} color={v2.sub} />
                    </XStack>
                  ))}
                </ListContainer>
              </YStack>
            ))}

            {hasMore && (
              <XStack
                ai="center"
                jc="center"
                py="$3"
                bg={v2.card}
                br={999}
                cursor="pointer"
                pressStyle={{ opacity: 0.7 }}
                style={{ boxShadow: "0 2px 10px -4px rgba(21, 101, 192, 0.10)" }}
                onPress={loadMore}>
                {loadingMore ? (
                  <Spinner color={colors.primary} />
                ) : (
                  <Text fontSize={13} fontWeight="700" color={v2.primary}>
                    載入更多
                  </Text>
                )}
              </XStack>
            )}
          </>
        )}

        <InfoNote>這裡顯示你經手（下單）的付款紀錄。點選任一筆可查看詳情與對應訂單。</InfoNote>
      </YStack>
    </SkinV2Screen>
  );
};

export default PayRecord;
